import React, { useMemo } from "react";
import { Empty, Spin } from "antd";
import {
  ExperienceViewer,
  ExperienceProvider,
  type ExperienceContextValue,
  type PlatformActions,
  MediaProjectInstanceResponse,
} from "@libertify/experience-viewer";
import { useProjectInstance } from "../hooks/useProjectInstance";
import { useTranslate } from "#src/hooks";
import { useAuth } from "../../../../hooks/useAuth";
import type {
  MediaProjectResponse,
  ProjectMediaResponse,
} from "pages/media/types";

const API_ROOT_URL = import.meta.env.VITE_API_BASE_URL ?? "";
const CHATBOT_URL = import.meta.env.VITE_CHATBOT_WS_URL ?? "";

interface CreatorPreviewProps {
  projectId: string;
  instanceId: string;
  mediaId?: string;
  organizationSlug?: string;
  instanceSlug?: string;
  creatorToolbar?: React.ReactNode;
  creatorToolbarMobile?: React.ReactNode;
  creatorFooter?: React.ReactNode;
  chatEnabled?: boolean;
  documentEnabled?: boolean;
  videoOverride?: React.ReactNode;
  isDocHeyGenius?: boolean;
  isAudioOnly?: boolean;
  isMediaGenerating?: boolean;
  project?: MediaProjectResponse;
  /** The project's own media (not the publication's) — stays fresh across a
   *  regeneration on a live instance, without needing a republish. */
  media?: ProjectMediaResponse;
}

export const CreatorPreview: React.FC<CreatorPreviewProps> = ({
  projectId,
  instanceId,
  mediaId,
  organizationSlug,
  instanceSlug,
  creatorToolbar,
  creatorToolbarMobile,
  creatorFooter,
  chatEnabled,
  documentEnabled,
  videoOverride,
  isDocHeyGenius,
  isAudioOnly,
  isMediaGenerating,
  project,
  media,
}) => {
  const t = useTranslate();
  const { accessToken } = useAuth();

  const organizationId = project?.organization_id;

  const { instance, isLoading } = useProjectInstance({
    projectId,
    instanceId,
    mediaId: mediaId ?? "",
  });

  // Project documents, not the instance's per-publication copies (MED-7046)
  const documentIds = useMemo(
    () => project?.documents.map((doc) => doc.id) ?? [],
    [project?.documents]
  );

  const actions = useMemo<PlatformActions>(
    () => ({
      getAuthCredentials: async () => ({
        bearerToken: accessToken ?? undefined,
        password: accessToken ?? undefined,
        documentIds,
      }),
    }),
    [accessToken, documentIds]
  );

  const contextValue = useMemo<ExperienceContextValue>(
    () => ({
      actions,
      config: {
        apiRootUrl: API_ROOT_URL,
        chatbotUrl: CHATBOT_URL,
        appPrefix: "libertify",
      },
    }),
    [actions]
  );

  // Build a minimal publication from project data so the document panel
  // can render even before the instance exists (e.g. during media generation).
  const fallbackPublication: MediaProjectInstanceResponse | undefined =
    useMemo(() => {
      if (!project || !isMediaGenerating) return undefined;
      const documentsMap: Record<string, (typeof project.documents)[number]> =
        {};
      project.documents.forEach((doc, index) => {
        documentsMap[`Document_${index}`] = doc;
      });
      const primaryDocId = project.settings.primary_document_id;
      return {
        project_id: project.id,
        instance_id: project.media[0]?.instance_id ?? "default",
        media: {
          id: project.media[0]?.id ?? "",
          organization: project.organization_id,
        } as MediaProjectInstanceResponse["media"],
        assets: {},
        documents: documentsMap,
        public: false,
        created: project.created ?? "",
        title: project.title,
        enable_chat: project.settings.chat_mode !== "Disabled",
        enable_chat_answers: true,
        document_animation: project.settings.include_document_animations
          ? {
              document_id: primaryDocId ?? "",
              cuepoints: null,
              highlights: null,
            }
          : null,
      };
    }, [project, isMediaGenerating]);

  const publication = useMemo(() => {
    if (!instance) return fallbackPublication;
    const pub = { ...instance };
    if (chatEnabled !== undefined) {
      pub.enable_chat = chatEnabled;
    }
    if (documentEnabled !== undefined && !documentEnabled) {
      pub.document_animation = null;
    }
    // Video source: the project's own media, not the publication's — for a
    // live instance the publication snapshot only refreshes on republish, so
    // it can keep serving the pre-regeneration video (see MED-6969).
    if (pub.media && media?.url) {
      pub.media = { ...pub.media, url: media.url };
    }
    return pub as unknown as Parameters<
      typeof ExperienceViewer
    >[0]["publication"];
  }, [instance, chatEnabled, documentEnabled, fallbackPublication, media?.url]);

  if (isLoading && !isMediaGenerating) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          padding: 48,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!instance && !isMediaGenerating) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Empty
          description={t("media.components.CreatorPreview.noPublicationData")}
        />
      </div>
    );
  }

  return (
    <ExperienceProvider value={contextValue}>
      <ExperienceViewer
        mode="creator"
        publication={publication}
        projectId={projectId}
        publicationId={instanceId}
        organizationId={organizationId}
        organizationSlug={organizationSlug}
        instanceSlug={instanceSlug}
        isPreview
        newMode
        creatorToolbar={creatorToolbar}
        creatorToolbarMobile={creatorToolbarMobile}
        creatorFooter={creatorFooter}
        videoOverride={videoOverride}
        isDocHeyGenius={isDocHeyGenius}
        isAudioOnly={isAudioOnly}
        isMediaGenerating={isMediaGenerating}
      />
    </ExperienceProvider>
  );
};
